import { FC } from 'react';
import { StyledTableCell, StyledTableRow } from 'ui/StyledTable';
import { DateTime } from 'luxon';
import { useLocation, useNavigate } from 'react-router';
import { Box, Button, Checkbox, Chip, Link, Typography } from '@mui/material';
import { EasTooltip } from 'ui/EasTooltip';
import { CheckBoxOutlineBlank } from '@mui/icons-material';
import queryString from 'query-string';
import { nanoid } from '@reduxjs/toolkit';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { TErrorCorrectionsItem } from 'redux/reducers/errorCorrections/types';
import { COLORS } from 'services/constants/colors';
import { useDispatch, useSelector } from 'react-redux';
import { getErrorTypes } from 'redux/reducers/errorCorrections/selectors';
import { StatusMenu } from 'ui/StatusMenu';
import { updateStatusRequest } from 'redux/reducers/errorCorrections/reducer';
import { errorsTableStructure } from '../errors-table-structure';
// import { TTableStructure } from '../types';
import { ETableStructureType, TTableStructure } from '../../../../ui/EasTable/types';
import { ErrorsStatus, representationSchemeType } from '../constants';
import { UserMenu } from './UserMenu';
import { EasTableValue } from '../../../../ui/EasTable/EasTableRow/EasTableValue';

export type THighlightSubstringProps = {
	str: string;
	substr: string;
};
export const HighlightSubstring: FC<THighlightSubstringProps> = ({ str, substr }) => {
	if (!substr) return <span>{str}</span>;
	const index = str.indexOf(substr);
	if (index === -1) return <span>{str}</span>;
	const prefix = str.substring(0, index);
	const suffix = str.substring(index + substr.length);

	return (
		<span>
			{prefix}
			<span style={{ backgroundColor: '#ffe900' }}>{substr}</span>
			{suffix}
		</span>
	);
};

export type TErrorsRowProps = {
	item: TErrorCorrectionsItem;
	setSelectedRow: (index: number | null) => void;
	index: number;
	selectedRow: number | null;
	filterId?: boolean;
	checkedRow?: boolean;
	setCheckRow: (checkedRow: boolean) => void;
};
export const ErrorsRow: FC<TErrorsRowProps> = ({
	item,
	setSelectedRow,
	index,
	selectedRow,
	filterId = false,
	checkedRow = false,
	setCheckRow,
}) => {
	const tableStructure = errorsTableStructure() as TTableStructure<keyof TErrorCorrectionsItem>[];
	const { search } = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const navigateParams = queryString.parse(search);

	const externalOrderId = navigateParams.external_order_id as string;
	const typeOfErrors = useSelector(getErrorTypes);

	const handleSetStatus = (status: ErrorsStatus) => {
		dispatch(updateStatusRequest({ errorId: +(item.id ?? 0), text: String(status) }));
	};

	return (
		<StyledTableRow
			key={nanoid()}
			sx={{
				'&:last-child td, &:last-child th': { border: 0 },
				background:
					// selectedRow === index && openAmountSpeedDial ? COLORS.TABLE_ROW_HOVER : 'auto',
					checkedRow
						? COLORS.TABLE_ROW_HOVER
						: selectedRow === index
							? COLORS.TABLE_ROW_HOVER
							: 'auto',
				// selectedRow === index ? '#03629338!important' : 'auto',
				p: '0px',
				m: '0px',
				cursor: 'pointer',
			}}
			// onClick={() => handleOrder(item?.id)}
			// onMouseOver={() => setSelectedRow(index)}
			onClick={() => navigate(`/error-correction/${item?.id}`)}
		>
			<StyledTableCell
				key={nanoid()}
				style={{
					padding: 0,
				}}
			>
				<Checkbox
					checked={checkedRow}
					sx={{ color: 'primary.main', m: 0, p: 0, pl: 1 }}
					onChange={(e) => {
						setCheckRow(e.target.checked);
						e.stopPropagation();
					}}
					onClick={(e) => {
						e.stopPropagation();
					}}
					// inputProps={{ 'aria-label': 'primary checkbox' }}
				/>
			</StyledTableCell>

			{tableStructure.map((filter) => {
				if (filter.show === false) return null;
				return (
					<StyledTableCell
						key={nanoid()}
						align={filter?.align ?? 'center'}
						colSpan={filter?.colSpan}
						sx={{
							maxWidth: '250px',
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							position: 'relative',
							p: '5px!important',
							m: '0px',
						}}
					>
						{/* {filter.selector === 'emName' ? (
							<EasTooltip title="View in EM Management">
								<Typography
									component="span"
									sx={{
										fontWeight: '550',
										color: 'primary.main',
										fontSize: '0.8rem',
										cursor: 'pointer',
									}}
								>
									<Link
										href={`/em-management/${String(item?.emId ?? '')}`}
										target="_blank"
										rel="noopener noreferrer"
										sx={{
											textDecoration: 'none',
										}}
										onClick={(e) => {
											e.stopPropagation();
										}}
									>
										{item?.emName}
									</Link>
								</Typography>
							</EasTooltip>
						) :  */}
						{filter.selector === 'id' ? (
							<span
								style={{
									backgroundColor: item?.id === +externalOrderId ? '#ffe900' : 'auto',
									padding: '2px 5px',
								}}
							>
								{item?.id}
							</span>
						) : // ) : filter.selector === 'externalOrderId' ? (
						// 	<HighlightSubstring str={item?.externalOrderId ?? ''} substr={externalOrderId} />
						filter.selector === 'typeOfError' ? (
							<Chip
								label={typeOfErrors?.find((i) => i.id === item?.typeOfError)?.name ?? 'Unknown'}
								size="small"
								color={!typeOfErrors?.find((i) => i.id === item?.typeOfError) ? 'default' : 'error'}
								sx={{
									p: '0 5px',
									fontSize: '.8rem',
									height: '20px',
									bgcolor: 'error.light',
									fontWeight: '550',
								}}
							/>
						) : filter.selector === 'status' ? (
							// <Box>
							// 	<Chip
							// 		label={item?.status ?? 'Unknown'}
							// 		size="small"
							// 		sx={{
							// 			p: '0 5px',
							// 			fontSize: '.8rem',
							// 			height: '20px',
							// 			bgcolor:
							// 				item?.status === ErrorsStatus.FIXED
							// 					? 'success.light'
							// 					: item?.status === ErrorsStatus.NEW
							// 					? 'warning.light'
							// 					: item?.status === ErrorsStatus.CONTACTING_CLIENT
							// 					? 'info.light'
							// 					: item?.status === ErrorsStatus.WORKING_ON_IT
							// 					? 'info.light'
							// 					: '#c5c5c5',
							// 			color: '#00000090',
							// 		}}
							// 	/>
							// 	<StatusMenu errorId={item.id} />
							// </Box>
							<StatusMenu status={item.status} onClick={handleSetStatus} size="xs" />
						) : filter.selector === 'orderDate' ? (
							<EasTableValue value={item?.orderDate} type={ETableStructureType.DATE} size="s" />
						) : filter.selector === 'deliveryCountry' ? (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									justifyContent: 'start',
									width: '100%',
									// gap: '5px',
								}}
							>
								<CountryWithFlag countryCode={item?.deliveryCountry?.split(' ')[0]} size="s" />
								{item?.deliveryCountry?.split(' ')?.[1] ? <span>,&nbsp;</span> : ''}
								<span>{item?.deliveryCountry?.split(' ')?.[1] ?? ''}</span>
							</Box>
						) : filter.selector === 'schemes' ? (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									justifyContent: 'start',
									width: '100%',
									gap: '5px',
								}}
							>
								{item.schemes?.split(',')?.map((scheme) => (
									<EasTableValue
										key={scheme}
										value={
											representationSchemeType?.[scheme as keyof typeof representationSchemeType] ??
											scheme
										}
										type={ETableStructureType.STATUS}
										size="s"
									/>
								))}
							</Box>
						) : filter?.selector === 'responsibleUser' ? (
							<UserMenu errorId={item.id} responsibleUser={item?.responsibleUser} />
						) : filter?.type === ETableStructureType.BUTTON ? (
							<Button variant="contained" size="small">
								File Report
							</Button>
						) : (
							// (item?.[filter?.selector] ?? '')
							<EasTableValue
								value={
									filter?.value
										? filter.value(item as TErrorCorrectionsItem)
										: (item?.[filter?.selector as keyof TErrorCorrectionsItem] ?? '')
								}
								type={filter?.type as ETableStructureType}
								size="s"
								tableStructureItem={filter}
								item={item}
							/>
						)}
					</StyledTableCell>
				);
			})}
		</StyledTableRow>
	);
};

// export const ConfirmedOrdersRowMemo = memo(ConfirmedOrdersRow);
